import React from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, Link, Typography } from "@mui/material";
import {
  AddRounded,
  ArrowForwardRounded,
  ListAltOutlined,
} from "@mui/icons-material";
import color from "../../../Constants/colors";
import { RegistrationStatus } from "../../../Models/ambassador";
import { useUser } from "../../../Query/useUsers";
import {
  useMyDeals,
  useMyDealSummary,
  useNews,
} from "../../../Query/useAmbassador";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  Panel,
  StatGrid,
  StatTile,
} from "../Common/AmbassadorUi";
import { ambassadorPaths } from "../Common/ambassadorNavigation";
import MyDealsTable from "../Deals/MyDealsTable";
import NewsTeaserList from "../News/NewsTeaserList";

const greetingKey = (hour: number) => {
  if (hour < 12) return "dashboard.greetingMorning";
  if (hour < 18) return "dashboard.greetingAfternoon";
  return "dashboard.greetingEvening";
};

const AmbassadorDashboard: React.FC = () => {
  const { t } = useTranslation("ambassador");
  const navigate = useNavigate();
  const { primary, accentText } = useDashboardColors();
  const { data: user } = useUser();
  const { data: summary } = useMyDealSummary();
  const deals = useMyDeals({ page: 1, page_size: 5 });
  const news = useNews({ page: 1, page_size: 3 });

  const allLink = (to: string, label: string) => (
    <Link
      component={RouterLink}
      to={to}
      underline="hover"
      sx={{
        fontSize: 13,
        fontWeight: 700,
        color: accentText,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        whiteSpace: "nowrap",
      }}
    >
      {label}
      <ArrowForwardRounded sx={{ fontSize: 16 }} />
    </Link>
  );

  return (
    <>
      <Typography
        component="h1"
        sx={{
          fontSize: 26,
          fontWeight: 800,
          color: primary,
          lineHeight: "34px",
        }}
      >
        {t(greetingKey(new Date().getHours()), { name: user?.name ?? "" })}
      </Typography>
      <Typography sx={{ mt: 0.5, mb: 3, color: color.gray500, fontSize: 14 }}>
        {t("dashboard.subtitle")}
      </Typography>

      <Box
        sx={{
          mb: 3,
          p: { xs: 2.5, md: 3 },
          borderRadius: "16px",
          backgroundColor: primary,
          display: "flex",
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            sx={{ color: color.white, fontSize: 20, fontWeight: 800 }}
          >
            {t("dashboard.ctaTitle")}
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.75)",
              fontSize: 14,
              mt: 0.5,
              maxWidth: 560,
            }}
          >
            {t("dashboard.ctaText")}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddRounded />}
          onClick={() => navigate(ambassadorPaths.newDeal)}
          sx={{ flexShrink: 0 }}
        >
          {t("dashboard.ctaButton")}
        </Button>
      </Box>

      {!!summary?.questions_pending && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() =>
                navigate(
                  `${ambassadorPaths.myDeals}?status=${RegistrationStatus.QUESTIONS_PENDING}`,
                )
              }
            >
              {t("dashboard.questionsAction")}
            </Button>
          }
        >
          {t("dashboard.questionsNotice", { count: summary.questions_pending })}
        </Alert>
      )}

      <StatGrid>
        <StatTile
          label={t("dashboard.stats.total")}
          value={summary?.total}
          hint={t("dashboard.stats.totalHint")}
        />
        <StatTile
          label={t("dashboard.stats.questions")}
          value={summary?.questions_pending}
          hint={t("dashboard.stats.questionsHint")}
          highlight={!!summary?.questions_pending}
        />
        <StatTile
          label={t("dashboard.stats.confirmed")}
          value={summary?.confirmed}
          hint={t("dashboard.stats.confirmedHint")}
        />
        <StatTile
          label={t("dashboard.stats.inSales")}
          value={summary?.in_sales}
          hint={t("dashboard.stats.inSalesHint")}
        />
      </StatGrid>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 1fr) 380px",
          },
          gap: 3,
          alignItems: "start",
        }}
      >
        <Panel
          title={t("dashboard.recentDeals")}
          action={allLink(ambassadorPaths.myDeals, t("dashboard.allDeals"))}
        >
          {deals.isLoading ? (
            <LoadingState />
          ) : deals.isError ? (
            <ErrorState onRetry={() => deals.refetch()} />
          ) : deals.data?.deals.length ? (
            <MyDealsTable deals={deals.data.deals} compact />
          ) : (
            <EmptyState
              icon={<ListAltOutlined />}
              title={t("myDeals.emptyTitle")}
              text={t("myDeals.emptyText")}
              action={
                <Button
                  variant="contained"
                  onClick={() => navigate(ambassadorPaths.newDeal)}
                >
                  {t("dashboard.ctaButton")}
                </Button>
              }
            />
          )}
        </Panel>

        <Panel
          title={t("navigation.news")}
          action={allLink(ambassadorPaths.news, t("dashboard.allNews"))}
        >
          {news.isLoading ? (
            <LoadingState />
          ) : news.isError ? (
            <ErrorState onRetry={() => news.refetch()} />
          ) : (
            <NewsTeaserList news={news.data?.news ?? []} />
          )}
        </Panel>
      </Box>
    </>
  );
};

export default AmbassadorDashboard;
